import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Shield, Network, Route, Lock, RefreshCw } from 'lucide-react';
import Tabs, { type Tab } from '../components/common/Tabs';
import WireGuardTab from '../components/connectors/WireGuardTab';
import RoutesTab from '../components/connectors/RoutesTab';
import NATTab from '../components/connectors/NATTab';
import ACLTab from '../components/connectors/ACLTab';
import { useToast } from '../components/common/ToastContainer';
import connectorService from '../services/connector.service';
import type { Connector } from '../services/connector.service';
import { useTenantStore } from '../stores/tenantStore';

const ConnectorConfig = () => {
  const { id } = useParams();
  const toast = useToast();
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);
  const [connector, setConnector] = useState<Connector | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const fetchConnector = async () => {
      if (!id) {
        setError('Connector ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await connectorService.getById(id);
        setConnector(data);
        setError(null);
      } catch (err: any) {
        console.error('Failed to load connector:', err);
        setError(err.message || 'Failed to load connector details');
      } finally {
        setLoading(false);
      }
    };

    fetchConnector();
  }, [id, selectedTenantId]);

  const handleSync = async () => {
    if (!id) return;
    try {
      setSyncing(true);
      const result = await connectorService.syncConfig(id);
      toast.success(`Config synced (${result.commandCount} commands, seq: ${result.seqNum})`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to sync connector config');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading connector configuration...</div>
      </div>
    );
  }

  if (error || !connector) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-600">{error || 'Connector not found'}</div>
      </div>
    );
  }

  const tabs: Tab[] = [
    { id: 'tunnel', label: 'Tunnel', icon: Lock, content: <WireGuardTab connectorId={id!} /> },
    { id: 'routes', label: 'Routes', icon: Route, content: <RoutesTab connectorId={id!} /> },
    { id: 'nat', label: 'NAT', icon: Network, content: <NATTab connectorId={id!} /> },
    { id: 'acl', label: 'ACL', icon: Shield, content: <ACLTab connectorId={id!} /> },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to={`/connectors/${id}`}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Connector Configuration</h1>
            <p className="text-gray-600 mt-1">{connector.connector_name}</p>
          </div>
        </div>
        <button
          onClick={handleSync}
          disabled={syncing}
          className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
          <span>{syncing ? 'Syncing...' : 'Sync Config'}</span>
        </button>
      </div>

      {/* Configuration Tabs */}
      <div className="card">
        <Tabs tabs={tabs} defaultTab="tunnel" />
      </div>
    </div>
  );
};

export default ConnectorConfig;
